import React, {
  useState,
  useEffect,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from "react";
import PropTypes from "prop-types";
import { motion, AnimatePresence } from "framer-motion";
import "../../App.css";
import { CONFIG } from "../../config/config";

const STORAGE_KEY = "table_page_size";
const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50, 75, 100];

const getSavedPageSize = (defaultSize) => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = parseInt(saved, 10);
      if (!isNaN(parsed) && DEFAULT_PAGE_SIZE_OPTIONS.includes(parsed)) {
        return parsed;
      }
    }
  } catch (e) {
    console.error("Error reading table_page_size from localStorage:", e);
  }
  return defaultSize || CONFIG.TABLE_PAGE_SIZE || 25;
};

const Table = forwardRef(
  (
    {
      data,
      columns,
      caption,
      textAlign = "left",
      onDataFiltered,
      selectedRowIds,
      onRowSelect,
      onSelectAll,
      isCheckBoxAvailable = false, // Added prop with default value
      pagination = true, // Pagination prop - default is false
      pageSize = CONFIG.TABLE_PAGE_SIZE, // Number of records per page
      pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
      onPageSizeChange,
      showPageSizeSelector = true,
      disableRowClickSelection = false, // New prop to disable selection on row click
      onRowClick,
      searchActions,
      leftSearchActions,
      serverSide = false,
      totalRecords,
      page: serverPage,
      sortBy: serverSortBy,
      sortOrder: serverSortOrder,
      onPageChange,
      onSortChange,
      onSearchChange,
    },
    ref,
  ) => {
    const [tableData, setTableData] = useState([]);
    const [sortConfig, setSortConfig] = useState({
      key: serverSortBy || null,
      direction:
        serverSortOrder === "desc" || serverSortOrder === "descending"
          ? "descending"
          : "ascending",
    });

    useEffect(() => {
      if (serverSide && serverSortBy) {
        setSortConfig({
          key: serverSortBy,
          direction:
            serverSortOrder === "desc" || serverSortOrder === "descending"
              ? "descending"
              : "ascending",
        });
      }
    }, [serverSide, serverSortBy, serverSortOrder]);
    const [searchTerm, setSearchTerm] = useState("");
    const [columnFilters, setColumnFilters] = useState({});
    const [openFilterColumn, setOpenFilterColumn] = useState(null);
    const [showSelectedOnly, setShowSelectedOnly] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [currentPageSize, setCurrentPageSize] = useState(() =>
      getSavedPageSize(pageSize),
    );

    useEffect(() => {
      const saved = getSavedPageSize(pageSize);
      setCurrentPageSize((prev) => (saved !== prev ? saved : prev));
    }, [pageSize]);

    const handlePageSizeChange = (e) => {
      const newSize = parseInt(e.target.value, 10);
      try {
        localStorage.setItem(STORAGE_KEY, newSize.toString());
      } catch (err) {
        console.error("Error saving table_page_size to localStorage:", err);
      }
      setCurrentPageSize(newSize);

      if (serverSide) {
        if (typeof onPageChange === "function") {
          onPageChange(1);
        }
      } else {
        setCurrentPage(1);
      }

      if (typeof onPageSizeChange === "function") {
        onPageSizeChange(newSize);
      }
    };

    const searchTimeoutRef = React.useRef(null);

    useEffect(() => {
      return () => {
        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current);
        }
      };
    }, []);

    const activePage = serverSide ? (serverPage || 1) : currentPage;

    const columnFilterValues = useMemo(() => {
      const filterValues = {};

      columns.forEach((column) => {
        if (column.filter) {
          const uniqueValues = [
            ...new Set(tableData.map((item) => item[column.key] || "")),
          ];
          filterValues[column.key] = uniqueValues
            .filter((val) => val !== "")
            .sort((a, b) => {
              const strA = String(a);
              const strB = String(b);
              if (strA === "-") return -1;
              if (strB === "-") return 1;
              return strA.localeCompare(strB, undefined, {
                numeric: true,
                sensitivity: "base",
              });
            });
        }
      });

      return filterValues;
    }, [tableData, columns]);

    // Reset to page 1 when search term or filters change (client-side only)
    useEffect(() => {
      if (!serverSide) {
        setCurrentPage(1);
      }
    }, [searchTerm, columnFilters, showSelectedOnly, serverSide]);

    // Track previous filtered IDs to avoid calling onDataFiltered when data hasn't actually changed
    const prevFilteredIdsRef = React.useRef(null);

    useEffect(() => {
      if (serverSide) {
        setTableData(data || []);
        if (typeof onDataFiltered === "function") {
          onDataFiltered(data || []);
        }
        return;
      }

      const lowercasedSearchTerm = searchTerm.toLowerCase();

      let filteredData = data.filter((item) => {
        const passesSearch = Object.values(item).some((val) =>
          val?.toString().toLowerCase().includes(lowercasedSearchTerm),
        );
        if (!passesSearch) return false;

        for (const [key, filterValue] of Object.entries(columnFilters)) {
          if (
            filterValue &&
            filterValue.length > 0 &&
            !filterValue.includes(item[key])
          ) {
            return false;
          }
        }
        return true;
      });

      if (sortConfig.key) {
        const columnDef = columns.find((c) => c.key === sortConfig.key);
        filteredData.sort((a, b) => {
          let aVal;
          let bVal;

          if (columnDef && typeof columnDef.sortBy === "function") {
            aVal = columnDef.sortBy(a);
            bVal = columnDef.sortBy(b);
          } else {
            aVal = a[sortConfig.key];
            bVal = b[sortConfig.key];
          }

          aVal = aVal ?? "";
          bVal = bVal ?? "";

          if (typeof aVal === "number" && typeof bVal === "number") {
            return sortConfig.direction === "ascending"
              ? aVal - bVal
              : bVal - aVal;
          }

          if (typeof aVal === "boolean" && typeof bVal === "boolean") {
            aVal = aVal ? 1 : 0;
            bVal = bVal ? 1 : 0;
            return sortConfig.direction === "ascending"
              ? aVal - bVal
              : bVal - aVal;
          }

          aVal = aVal.toString();
          bVal = bVal.toString();

          const compareResult = aVal.localeCompare(bVal, undefined, {
            numeric: true,
            sensitivity: "base",
          });
          
          return sortConfig.direction === "ascending" ? compareResult : -compareResult;
        });
      }

      if (typeof onDataFiltered === "function") {
        const currentIds = filteredData.map((item) => item._id).join(",");
        if (prevFilteredIdsRef.current !== currentIds) {
          prevFilteredIdsRef.current = currentIds;
          onDataFiltered(filteredData);
        }
      }

      let finalData = filteredData;
      if (showSelectedOnly) {
        finalData = filteredData.filter((item) =>
          selectedRowIds?.has(item._id),
        );
      }

      setTableData(finalData);
    }, [
      searchTerm,
      data,
      columnFilters,
      sortConfig,
      onDataFiltered,
      showSelectedOnly,
      selectedRowIds,
      columns,
      serverSide,
    ]);

    const sortData = (key) => {
      let direction = "ascending";
      if (sortConfig.key === key && sortConfig.direction === "ascending") {
        direction = "descending";
      }
      setSortConfig({ key, direction });
      if (serverSide && typeof onSortChange === "function") {
        onSortChange(key, direction);
      }
    };

    const renderCell = (item, column) => {
      if (column.render) return column.render(item);
      return item[column.key] || "-";
    };

    const toggleFilterDropdown = (columnKey) => {
      setOpenFilterColumn(openFilterColumn === columnKey ? null : columnKey);
    };

    const handleFilterChange = (columnKey, value) => {
      setColumnFilters((prev) => {
        const currentValues = prev[columnKey] || [];
        const newValues = currentValues.includes(value)
          ? currentValues.filter((val) => val !== value)
          : [...currentValues, value];

        return {
          ...prev,
          [columnKey]: newValues,
        };
      });
    };

    const clearFilter = (columnKey) => {
      setColumnFilters((prev) => ({
        ...prev,
        [columnKey]: [],
      }));
    };

    // Pagination logic
    const effectivePageSize =
      currentPageSize || pageSize || CONFIG.TABLE_PAGE_SIZE || 25;
    const totalCount = serverSide ? (totalRecords || 0) : tableData.length;
    const totalPages = serverSide
      ? Math.ceil(totalCount / effectivePageSize)
      : (pagination ? Math.ceil(tableData.length / effectivePageSize) : 1);

    const startIndex = serverSide
      ? (activePage - 1) * effectivePageSize
      : (pagination ? (currentPage - 1) * effectivePageSize : 0);

    const endIndex = serverSide
      ? startIndex + tableData.length
      : (pagination ? startIndex + effectivePageSize : tableData.length);

    const paginatedData = serverSide || !pagination
      ? tableData
      : tableData.slice(startIndex, endIndex);

    const visibleItemIds = isCheckBoxAvailable
      ? paginatedData.map((item) => item._id)
      : [];
    const areAllVisibleSelected =
      isCheckBoxAvailable &&
      visibleItemIds.length > 0 &&
      visibleItemIds.every((id) => selectedRowIds?.has(id));
    const isIndeterminate =
      isCheckBoxAvailable &&
      visibleItemIds.some((id) => selectedRowIds?.has(id)) &&
      !areAllVisibleSelected;

    const handleSelectAllClick = (e) => {
      e.stopPropagation();
      if (typeof onSelectAll === "function" && isCheckBoxAvailable) {
        onSelectAll(visibleItemIds, areAllVisibleSelected);
      }
    };

    const toggleShowSelectedOnly = (e) => {
      e.stopPropagation();
      if (isCheckBoxAvailable) {
        setShowSelectedOnly((prev) => !prev);
      }
    };

    // Pagination handlers
    const goToPage = (page) => {
      if (page >= 1 && page <= totalPages) {
        if (serverSide && typeof onPageChange === "function") {
          onPageChange(page);
        } else {
          setCurrentPage(page);
        }
      }
    };

    const goToFirstPage = () => goToPage(1);
    const goToPreviousPage = () => goToPage(activePage - 1);
    const goToNextPage = () => goToPage(activePage + 1);
    const goToLastPage = () => goToPage(totalPages);

    // Generate page numbers for pagination UI
    const getPageNumbers = () => {
      const pages = [];
      const maxVisiblePages = 5;

      if (totalPages <= maxVisiblePages) {
        for (let i = 1; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);

        let startPage = Math.max(2, activePage - 1);
        let endPage = Math.min(totalPages - 1, activePage + 1);

        if (activePage <= 3) {
          endPage = 4;
        }

        if (activePage >= totalPages - 2) {
          startPage = totalPages - 3;
        }

        if (startPage > 2) {
          pages.push("...");
        }

        for (let i = startPage; i <= endPage; i++) {
          pages.push(i);
        }

        // Add ellipsis if needed
        if (endPage < totalPages - 1) {
          pages.push("...");
        }

        // Always show last page
        pages.push(totalPages);
      }

      return pages;
    };

    const tableVariants = {
      hidden: { opacity: 0, y: 20 },
      visible: { opacity: 1, y: 0 },
    };

    const rowVariants = {
      hidden: { opacity: 0, x: -20 },
      visible: { opacity: 1, x: 0 },
    };

    // Expose setFilter method to parent via ref
    useImperativeHandle(ref, () => ({
      setFilter: (columnKey, value) => {
        setColumnFilters((prev) => ({
          ...prev,
          [columnKey]: value ? [value] : [],
        }));
      },
      clearAllFilters: () => {
        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current);
        }
        setColumnFilters({});
        setSearchTerm("");
        if (serverSide && typeof onSearchChange === "function") {
          onSearchChange("");
        }
      },
    }));

    return (
      <motion.div
        className="table-container"
        initial="hidden"
        animate="visible"
        variants={tableVariants}
        style={{ textAlign: "left" }}
      >
        <motion.div
          className="table-search"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
          style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginRight: "1rem" }}
        >
          <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
            <motion.input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => {
                const val = e.target.value;
                setSearchTerm(val);
                if (serverSide && typeof onSearchChange === "function") {
                  if (searchTimeoutRef.current) {
                    clearTimeout(searchTimeoutRef.current);
                  }
                  searchTimeoutRef.current = setTimeout(() => {
                    onSearchChange(val);
                  }, 800);
                }
              }}
              className="search-input"
            />
            {leftSearchActions}
          </div>
          {searchActions}
        </motion.div>
        <motion.div
          className="table-wrapper glass"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <table>
            {caption && (
              <motion.caption
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                {caption}
              </motion.caption>
            )}
            <thead>
              <tr>
                {isCheckBoxAvailable && (
                  <motion.th className="checkbox-column-header">
                    <div className="th-content select-all-header">
                      <input
                        type="checkbox"
                        checked={areAllVisibleSelected}
                        ref={(el) => el && (el.indeterminate = isIndeterminate)}
                        onChange={handleSelectAllClick}
                        title={
                          areAllVisibleSelected
                            ? "Deselect All Visible"
                            : "Select All Visible"
                        }
                      />
                      <button
                        className={`filter-button show-selected-button ${
                          showSelectedOnly ? "active" : ""
                        }`}
                        onClick={toggleShowSelectedOnly}
                        title={
                          showSelectedOnly ? "Show All" : "Show Selected Only"
                        }
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill={showSelectedOnly ? "currentColor" : "none"}
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                        </svg>
                        {selectedRowIds?.size > 0 && (
                          <span className="filter-count">
                            {selectedRowIds.size}
                          </span>
                        )}
                      </button>
                    </div>
                  </motion.th>
                )}
                {columns.map((column) => {
                  const alignment =
                    column.textAlign || column.align || textAlign;
                  return (
                    <motion.th
                      key={column.key}
                      onClick={() =>
                        column.sortable !== false && sortData(column.key)
                      }
                      className={`
                      ${sortConfig.key === column.key ? sortConfig.direction : ""}
                      ${column.sortable !== false ? "sortable" : ""}
                      ${column.filter ? "filterable" : ""}
                    `}
                      style={{
                        textAlign: alignment,
                        ...(column.headerStyle || column.thStyle || {}),
                      }}
                    >
                      <div className="th-content">
                        <span
                          className="column-label"
                          style={{ textAlign: alignment }}
                        >
                          {column.label}
                        </span>

                      {column.filter && (
                        <div className="filter-container">
                          <button
                            className={`filter-button ${
                              columnFilters[column.key]?.length > 0
                                ? "active"
                                : ""
                            }`}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleFilterDropdown(column.key);
                            }}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                            </svg>
                            {columnFilters[column.key]?.length > 0 && (
                              <span className="filter-count">
                                {columnFilters[column.key].length}
                              </span>
                            )}
                          </button>

                          {openFilterColumn === column.key && (
                            <div className="filter-dropdown">
                              <div className="filter-dropdown-header">
                                <span>Filter by {column.label}</span>
                                <button
                                  className="clear-filter"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    clearFilter(column.key);
                                  }}
                                >
                                  Clear
                                </button>
                              </div>
                              <div className="filter-options">
                                {columnFilterValues[column.key]?.map(
                                  (value) => (
                                    <label
                                      key={value}
                                      className="filter-option"
                                    >
                                      <input
                                        type="checkbox"
                                        checked={
                                          columnFilters[column.key]?.includes(
                                            value,
                                          ) || false
                                        }
                                        onChange={() =>
                                          handleFilterChange(column.key, value)
                                        }
                                      />
                                      <span style={{ textTransform: "none" }}>
                                        {value}
                                      </span>
                                    </label>
                                  ),
                                )}
                                {columnFilterValues[column.key]?.length ===
                                  0 && (
                                  <div className="no-filter-options">
                                    No values to filter
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </motion.th>
                );
              })}
              </tr>
            </thead>
            <AnimatePresence mode="wait">
              <tbody>
                {paginatedData.length > 0 ? (
                  paginatedData.map((item, index) => {
                    const isSelected =
                      isCheckBoxAvailable && selectedRowIds?.has(item._id);
                    return (
                      <motion.tr
                        key={item._id || index}
                        variants={rowVariants}
                        initial="hidden"
                        animate="visible"
                        exit={{ opacity: 0, x: 20 }}
                        transition={{ delay: index * 0.03 }}
                        whileHover={
                          typeof onRowClick === "function"
                            ? { backgroundColor: "rgba(240, 154, 33, 0.06)" }
                            : {}
                        }
                        className={`${
                          isCheckBoxAvailable && isSelected
                            ? "selected-row"
                            : ""
                        } ${typeof onRowClick === "function" ? "clickable-row" : ""} ${item._rowClassName || ""}`}
                         style={{
                          cursor:
                            (isCheckBoxAvailable && !disableRowClickSelection) || typeof onRowClick === "function"
                              ? "pointer"
                              : "default",
                          ...(item._rowStyle || {}),
                        }}
                        onClick={(e) => {
                          const target = e.target;
                          const isInteractiveElement =
                            target.closest("button") ||
                            target.closest("a") ||
                            target.closest("input") ||
                            target.closest("select") ||
                            target.closest("textarea") ||
                            target.closest(".view-btn") ||
                            target.closest(".edit-btn") ||
                            target.closest(".delete-btn") ||
                            target.closest(".btn-download-qr");

                          if (isInteractiveElement) return;

                          if (typeof onRowClick === "function") {
                            onRowClick(item, e);
                            return;
                          }

                          if (disableRowClickSelection) return;

                          if (
                            isCheckBoxAvailable &&
                            typeof onRowSelect === "function"
                          ) {
                            onRowSelect(null, item._id);
                          }
                        }}
                      >
                        {isCheckBoxAvailable && (
                          <td className="checkbox-cell">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => {
                                e.stopPropagation();
                                if (typeof onRowSelect === "function") {
                                  onRowSelect(e, item._id);
                                }
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </td>
                        )}
                        {columns.map((column) => {
                          const alignment =
                            column.textAlign || column.align || textAlign;
                          return (
                            <td
                              key={`${item._id || index}-${column.key}`}
                              className={
                                column.key === "questionText"
                                  ? "question-text-cell"
                                  : ""
                              }
                              style={{
                                textAlign: alignment,
                                ...(column.cellStyle || column.style || {}),
                              }}
                            >
                              {renderCell(item, column)}
                            </td>
                          );
                        })}
                      </motion.tr>
                    );
                  })
                ) : (
                  <motion.tr initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                    <td
                      colSpan={
                        isCheckBoxAvailable
                          ? columns.length + 1
                          : columns.length
                      }
                    >
                      <motion.div
                        className="empty-state"
                        initial={{ scale: 0.9 }}
                        animate={{
                          scale: 1,
                          transition: {
                            type: "spring",
                            stiffness: 200,
                            damping: 15,
                          },
                        }}
                      >
                        No matching records found
                      </motion.div>
                    </td>
                  </motion.tr>
                )}
              </tbody>
            </AnimatePresence>
          </table>
        </motion.div>

        {/* Pagination UI */}
        {pagination && totalPages > 0 && (
          <motion.div
            className="pagination-container"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <div className="pagination-info">
              Showing {totalCount > 0 ? startIndex + 1 : 0} -{" "}
              {Math.min(startIndex + paginatedData.length, totalCount)} of {totalCount}{" "}
              records
            </div>

            <div className="pagination-controls">
              {/* First Page Button */}
              <button
                className="pagination-btn pagination-nav-btn"
                onClick={goToFirstPage}
                disabled={activePage === 1}
                title="First Page"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="11 17 6 12 11 7"></polyline>
                  <polyline points="18 17 13 12 18 7"></polyline>
                </svg>
              </button>

              {/* Previous Page Button */}
              <button
                className="pagination-btn pagination-nav-btn"
                onClick={goToPreviousPage}
                disabled={activePage === 1}
                title="Previous Page"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="15 18 9 12 15 6"></polyline>
                </svg>
              </button>

              {/* Page Numbers */}
              <div className="pagination-pages">
                {getPageNumbers().map((page, index) =>
                  page === "..." ? (
                    <span
                      key={`ellipsis-${index}`}
                      className="pagination-ellipsis"
                    >
                      ...
                    </span>
                  ) : (
                    <button
                      key={page}
                      className={`pagination-btn pagination-page-btn ${
                        activePage === page ? "active" : ""
                      }`}
                      onClick={() => goToPage(page)}
                    >
                      {page}
                    </button>
                  ),
                )}
              </div>

              {/* Next Page Button */}
              <button
                className="pagination-btn pagination-nav-btn"
                onClick={goToNextPage}
                disabled={activePage === totalPages}
                title="Next Page"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
              </button>

              {/* Last Page Button */}
              <button
                className="pagination-btn pagination-nav-btn"
                onClick={goToLastPage}
                disabled={activePage === totalPages}
                title="Last Page"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="13 17 18 12 13 7"></polyline>
                  <polyline points="6 17 11 12 6 7"></polyline>
                </svg>
              </button>
            </div>

            <div className="pagination-right">
              {serverSide && showPageSizeSelector && (
                <div className="pagination-page-size">
                  <label htmlFor="table-page-size-select" className="page-size-label">
                    Records:
                  </label>
                  <select
                    id="table-page-size-select"
                    className="page-size-select"
                    value={effectivePageSize}
                    onChange={handlePageSizeChange}
                  >
                    {pageSizeOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="pagination-page-info">
                Page {activePage} of {totalPages}
              </div>
            </div>
          </motion.div>
        )}
      </motion.div>
    );
  },
);

Table.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
      render: PropTypes.func,
      sortable: PropTypes.bool,
      textAlign: PropTypes.string,
      align: PropTypes.string,
      filter: PropTypes.bool,
      headerStyle: PropTypes.object,
      cellStyle: PropTypes.object,
      style: PropTypes.object,
    }),
  ),
  caption: PropTypes.string,
  textAlign: PropTypes.string,
  onDataFiltered: PropTypes.func,
  selectedRowIds: PropTypes.instanceOf(Set),
  onRowSelect: PropTypes.func,
  onSelectAll: PropTypes.func,
  isCheckBoxAvailable: PropTypes.bool,
  pagination: PropTypes.bool,
  pageSize: PropTypes.number,
  pageSizeOptions: PropTypes.arrayOf(PropTypes.number),
  onPageSizeChange: PropTypes.func,
  showPageSizeSelector: PropTypes.bool,
  disableRowClickSelection: PropTypes.bool,
  onRowClick: PropTypes.func,
  searchActions: PropTypes.node,
  leftSearchActions: PropTypes.node,
  serverSide: PropTypes.bool,
  totalRecords: PropTypes.number,
  page: PropTypes.number,
  sortBy: PropTypes.string,
  sortOrder: PropTypes.string,
  onPageChange: PropTypes.func,
  onSortChange: PropTypes.func,
  onSearchChange: PropTypes.func,
};

export default Table;
